import Reflection from "nexia_mobile/framework/reflection";
import { getLanguageSet, template } from "nx-components/Locales";

export const VALIDATION_PATTERNS = {
  email: /^(['+\-.\w]+)@((\w[+\-.\w]*)\.[\w]{2,})$/,
  number: /\d+/,
  // US 5+4 and CAN postal validation
  postal: /(^\d{5}$)|(^\d{5}\s{0,}-?\s{0,}\d{4}$)|(^[a-ceghj-npr-tvxy]\d[a-ceghj-npr-tv-z]\s{0,}-?\s{0,}\d[a-ceghj-npr-tv-z]\d$)/i,
};

export default {
  _validateAttributes(attrs, options = {}) {
    const language = getLanguageSet().nexia_mobile.validatable;
    let validationDefs = Reflection.evaluate(this.validations, this);
    this._validationErrors = {};
    this._validatedAttributes = {};

    // we can skip validations based on the attribute name

    let _shouldValidate;
    if (options.validateOnly != null) {
      if (options.validateOnly instanceof RegExp) {
        _shouldValidate = (attr) => options.validateOnly.test(attr);
      } else if (_.isArray(options.validateOnly)) {
        _shouldValidate = (attr) => _.contains(options.validateOnly, attr);
      } else if (_.isString(options.validateOnly)) {
        _shouldValidate = (attr) => options.validateOnly === attr;
      } else if (_.isFunction(options.validateOnly)) {
        _shouldValidate = options.validateOnly;
      }
    }
    _shouldValidate = _shouldValidate || (() => true);

    // create of map of fields whose validations depend on other fields

    let validationDependency = {};
    /* eslint-disable guard-for-in */
    for (let attr in validationDefs) {
      let validation = validationDefs[attr];
      if (_.isString(validation.equalTo)) {
        validationDependency[attr] = validation.equalTo;
        validationDependency[validation.equalTo] = attr;
      }
    }
    /* eslint-enable guard-for-in */

    // now run each validation

    /* eslint-disable guard-for-in */
    for (let attr in validationDefs) {
      let validation = validationDefs[attr];
      let hasDependency =
        validationDependency[attr] != null &&
        _shouldValidate(validationDependency[attr]);
      if (!_shouldValidate(attr) && !hasDependency) {
        continue;
      }
      this._validatedAttributes[attr] = true;

      // get the value to be validated

      let value = Reflection.getNestedAttribute(attrs, attr);

      // run a custom validator

      if (_.isFunction(validation)) {
        validation = { fn: validation };
      }
      if (_.isString(validation.fn)) {
        validation.fn = this[validation.fn];
      }

      if (_.isFunction(validation.fn)) {
        let message = validation.fn.call(this, value, attr);
        if (_.isString(message)) {
          this._addValidationError(attr, message);
        }
        continue;
      }

      // determine label to use for error messages

      let label = Reflection.evaluate(validation.label, this);
      let errorLabel = Reflection.evaluate(validation.errorLabel, this);
      label = errorLabel || label || attr;

      // A specifcially-tailored message for required fields
      let requiredMessage = Reflection.evaluate(
        validation.requiredMessage,
        this
      );

      // deal with required attributes
      let required = Reflection.evaluate(validation.required, this);
      required = required != null ? required : false;
      if (required) {
        if (
          value == null ||
          (validation.type &&
            validation.type === "boolean" &&
            value === false) ||
          (_.isString(value) && _.str.isBlank(value))
        ) {
          this._addValidationError(
            attr,
            requiredMessage ||
              template(language.labelRequiredTemplate, { label })
          );
          continue;
        }
      }

      // equality with another field
      if (_.isString(validation.equalTo)) {
        if (
          value !== Reflection.getNestedAttribute(attrs, validation.equalTo)
        ) {
          this._addValidationError(
            attr,
            template(language.labelDoesNotMatchTemplate, { label })
          );
        }
      }

      if (!required && (value == null || value.toString().trim() === "")) {
        // If the field is not required, it is allowed to be empty even if that fails other validations
        continue;
      }

      // test against regexes
      if (_.isString(validation.patternType)) {
        validation.pattern = VALIDATION_PATTERNS[validation.patternType];
      }

      if (_.isString(validation.pattern)) {
        validation.pattern = new RegExp(validation.pattern);
      }

      let stringValue = (value != null ? value : "").toString();
      if (validation.pattern instanceof RegExp) {
        if (!stringValue.match(validation.pattern)) {
          this._addValidationError(
            attr,
            validation.message ||
              template(language.labelInvalidTemplate, { label })
          );
        }
      }

      // string length

      if (_.isNumber(validation.length)) {
        if (!_.isString(value) || value.length !== validation.length) {
          this._addValidationError(
            attr,
            template(language.labelExactLengthCharsTemplate, {
              label,
              fieldLength: validation.length,
            })
          );
        }
      }

      if (_.isNumber(validation.minLength)) {
        if (!_.isString(value) || value.length < validation.minLength) {
          this._addValidationError(
            attr,
            template(language.labelMinLengthCharsTemplate, {
              label,
              fieldMax: validation.minLength,
            })
          );
        }
      }

      if (_.isNumber(validation.maxLength)) {
        if (_.isString(value) && value.length > validation.maxLength) {
          this._addValidationError(
            attr,
            template(language.labelMaxLengthCharsTemplate, {
              label,
              fieldMaxLength: validation.maxLength,
            })
          );
        }
      }

      // numerical values

      if (_.isNumber(validation.min)) {
        if (value < validation.min) {
          this._addValidationError(
            attr,
            template(language.labelMinNumberTemplate, {
              label,
              fieldMin: validation.min,
            })
          );
        }
      }

      if (_.isNumber(validation.max)) {
        if (value > validation.max) {
          this._addValidationError(
            attr,
            template(language.labelMaxNumberTemplate, {
              label,
              fieldMax: validation.max,
            })
          );
        }
      }
    }
    /* eslint-enable guard-for-in */

    if (!_.isEmpty(this._validationErrors)) {
      return this._validationErrors;
    } else {
      return null;
    }
  },

  _addValidationError(attr, message) {
    this._validationErrors[attr] = this._validationErrors[attr] || [];
    this._validationErrors[attr].push(message);
  },

  wasAttributeValidated(attr) {
    if (this._validatedAttributes != null) {
      return this._validatedAttributes[attr] != null;
    } else {
      return false;
    }
  },
};



// WEBPACK FOOTER //
// ./src/framework/validatable.js